import type { MaterialGroupBy } from 'common/models';
import MaterialTableHeader from 'components/Material/MaterialTableHeader';
import MaterialTableRow from 'components/Material/MaterialTableRow';
import { sortGroupKeys } from 'helpers/comparing';
import {
  getMaterialGroupByKey,
  getMaterialGroupedRowTitle,
  getMaterialPriceColSpan
} from 'helpers/materialHelpers';
import { valueCurrencyShortcut } from 'helpers/priceHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import groupBy from 'lodash.groupby';
import type { OrderMaterial } from 'pages/orders/models';
import React from 'react';

interface OwnProps {
  material: OrderMaterial[];
  title?: string;
  currencyId: string;
  showPrice: boolean;
  groupByKey?: MaterialGroupBy;
  visibleColumns?: string[];
}

const MaterialBlock = (props: OwnProps) => {
  const { material, currencyId, title, showPrice, groupByKey, visibleColumns } = props;
  const { t } = useCustomTranslation();

  const materialSummary = material.reduce((acc, cur) => {
    return acc + cur.amount * (cur.price || 0);
  }, 0);

  const renderGroupByTable = () => {
    const groupedMaterial = groupBy(material, (material) =>
      getMaterialGroupByKey(material, groupByKey)
    );

    return Object.keys(groupedMaterial)
      .sort((a) => sortGroupKeys(a))
      .map((groupKey) => {
        return (
          <tbody key={groupKey} className="grouped">
            <tr className="groupHeading" key={groupKey}>
              <td colSpan={9}>
                {getMaterialGroupedRowTitle(
                  groupedMaterial[groupKey][0],
                  groupKey as MaterialGroupBy
                )}
              </td>
            </tr>
            {groupedMaterial[groupKey].map((material) => (
              <MaterialTableRow
                key={material.id}
                currencyId={currencyId}
                material={material}
                groupByKey={groupByKey}
                showPrice={showPrice}
                visibleColumns={visibleColumns}
              />
            ))}
          </tbody>
        );
      });
  };

  return (
    <div className="MaterialBlock">
      <div className="SectionTitle">{title}</div>
      {material.length > 0 ? (
        <table>
          <MaterialTableHeader
            groupByKey={groupByKey}
            showPrice={showPrice}
            visibleColumns={visibleColumns}
          />
          {groupByKey && renderGroupByTable()}
          {!groupByKey && (
            <tbody>
              {material.map((material) => (
                <MaterialTableRow
                  key={material.id}
                  currencyId={currencyId}
                  material={material}
                  groupByKey={groupByKey}
                  showPrice={showPrice}
                  visibleColumns={visibleColumns}
                />
              ))}
            </tbody>
          )}
          {showPrice && (
            <tbody>
              <tr>
                <td colSpan={getMaterialPriceColSpan(visibleColumns, groupByKey)} />
                <td className="priceCol">
                  {valueCurrencyShortcut(materialSummary, currencyId, t)}
                </td>
              </tr>
            </tbody>
          )}
        </table>
      ) : (
        <div className="EmptyPlaceholder">{t('customData.material.empty')}</div>
      )}
    </div>
  );
};

export default MaterialBlock;
